/** @jsxImportSource @emotion/react */
"use client";

import type { NiceModalHandler } from "@ebay/nice-modal-react";
import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import * as RadixUIDialog from "@radix-ui/react-dialog";
import { HallowDataTestId } from "@tools/playwright/global";
import type {
  ComponentProps,
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
} from "react";
import { Fragment, useRef, useState } from "react";

import {
  numericPercentages,
  numericPixels,
  spacing,
  zIndices,
} from "../../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import { createModal, useModal } from "../../../../niceModal";
import type { ButtonProps } from "../../_core/Button/Button.emotion";
import { Button } from "../../_core/Button/Button.emotion";
import type { IconButtonProps } from "../../_core/IconButton/IconButton.emotion";
import { IconButton } from "../../_core/IconButton/IconButton.emotion";
import type { InputProps } from "../../_core/Input/Input.emotion";
import { Input } from "../../_core/Input/Input.emotion";
import { Text } from "../../_core/Text/Text.emotion";
import { CloseIcon } from "../../icons/Icon/NavigationIcons";
import type { ModalProps } from "../Modal/Modal.emotion";
import { Modal } from "../Modal/Modal.emotion";

const contentStyle: Interpolation = {
  maxHeight: "95vh",
  overflowY: "auto",
  padding: spacing.xl,
  width: numericPercentages[100],
  zIndex: zIndices.modalOverModal,
  [cssMediaQueryMinWidthTablet]: {
    padding: spacing.xl,
  },
};
const overlayStyle: Interpolation = { zIndex: zIndices.overlayOverModal };
const titleStyle: Interpolation = {
  flex: 1,
  margin: 0,
};
const descStyle: Interpolation = {
  marginTop: spacing.xxs,
  whiteSpace: "pre-line",
};
const childrenStyle: Interpolation = {
  marginTop: spacing.m,
};
const buttonsStyle: Interpolation = {
  display: "flex",
  justifyContent: "center",
};
const buttonsTwoStyle: Interpolation = {
  justifyContent: "space-between",
};
const buttonsManyStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
};
const reverseOrderStyle: Interpolation = {
  flexDirection: "column-reverse",
};
const headerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: 10,
};
const headerNoCloseButtonStyle: Interpolation = {
  marginBottom: spacing.xxs,
};

const sizeStyles: Record<
  `${DialogProps["size"]}${"Buttons" | "Content"}`,
  Interpolation
> = {
  sButtons: {
    marginTop: spacing.m,
  },
  mButtons: {
    marginTop: spacing.l,
  },
  lButtons: {
    marginTop: spacing.l,
  },
  sContent: {
    maxWidth: numericPixels[360],
  },
  mContent: {
    maxWidth: numericPixels[400],
  },
  lContent: {
    maxWidth: numericPixels[600],
  },
};

type DialogProps = ComponentProps<typeof RadixUIDialog.Root> & {
  buttons: ReactElement<ButtonProps>[];
  description: ReactNode | undefined;
  modalProps?: ModalProps;
  showCloseButton?: boolean;
  size?: "s" | "m" | "l";
  title: ReactNode;
  buttonFormat?: "vertical" | "horizontal" | "vertical-reverse";
  iconButtonProps?: Partial<IconButtonProps>;
};
type Modal = NiceModalHandler<Record<string, unknown>>;

export const Dialog = ({
  buttons,
  children,
  description,
  modalProps,
  onOpenChange,
  open,
  showCloseButton = true,
  size = "s",
  title,
  buttonFormat = "horizontal",
  iconButtonProps = {},
}: DialogProps) => {
  const buttonsCount = buttons.filter((button) => !!button).length;
  const handleOpenChange = (open) => {
    if (onOpenChange) {
      onOpenChange(open);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      {...modalProps}
      contentCSS={[
        contentStyle,
        sizeStyles[`${size}Content`],
        modalProps?.contentCSS,
      ]}
      overlayCSS={[overlayStyle, modalProps?.overlayCSS]}
    >
      <div css={[headerStyle, !showCloseButton && headerNoCloseButtonStyle]}>
        <RadixUIDialog.Title asChild>
          <Text size="l" type="title" as="h3" css={titleStyle}>
            {title}
          </Text>
        </RadixUIDialog.Title>
        {showCloseButton && (
          <IconButton
            icon={<CloseIcon />}
            variant="transparentPrimary"
            onClick={() => handleOpenChange(false)}
            {...iconButtonProps}
          />
        )}
      </div>
      {description && (
        <RadixUIDialog.Description asChild>
          <Text size="m" type="body" css={descStyle}>
            {description}
          </Text>
        </RadixUIDialog.Description>
      )}
      {children && <div css={childrenStyle}>{children}</div>}
      {buttonsCount > 0 && (
        <div
          css={[
            buttonsStyle,
            sizeStyles[`${size}Buttons`],
            buttonsCount === 2 &&
              buttonFormat === "horizontal" &&
              buttonsTwoStyle,
            (buttonsCount > 2 ||
              buttonFormat === "vertical" ||
              buttonFormat === "vertical-reverse") &&
              buttonsManyStyle,
            buttonFormat === "vertical-reverse" && reverseOrderStyle,
          ]}
        >
          {buttons.map((button, index) => (
            <Fragment key={index}>{button}</Fragment>
          ))}
        </div>
      )}
    </Modal>
  );
};

interface BaseDialogProps<T> {
  title?: (props: T) => ReactNode;
  description?: (props: T) => ReactNode;
  modalProps?: ModalProps;
  showCloseButton?: boolean;
  size?: DialogProps["size"];
  buttonFormat?: DialogProps["buttonFormat"];
  iconButtonProps?: DialogProps["iconButtonProps"];
}

type State = Record<string, any>;
type StatefulModal = Modal & {
  state: State;
  setState: Dispatch<SetStateAction<State>>;
};
type DialogButton<T> = (
  props: T,
  modal: StatefulModal,
) => ReactElement<ButtonProps>;
export interface CreateDialogProps<T> extends BaseDialogProps<T> {
  buttons?: DialogButton<T>[];
  children?: (props: T, modal: StatefulModal) => React.ReactNode;
  onOpenChange?: (open: boolean, props: T, modal: StatefulModal) => void;
}

export function useDialog<T>({
  title,
  description,
  modalProps,
  buttons = [],
  children,
  onOpenChange,
  showCloseButton = true,
  iconButtonProps = {},
  ...dialogProps
}: CreateDialogProps<T>) {
  const modal = createModal<T>((props) => {
    const modal = useModal();
    const [state, setState] = useState<Record<string, any>>({});
    const statefulModal = { state, setState, ...modal };

    return (
      <Dialog
        title={title?.(props)}
        description={description?.(props)}
        open={modal.visible}
        onOpenChange={(open) => {
          if (open) modal.show();
          else {
            modal.resolve();
            modal.remove();
          }
          onOpenChange?.(open, props, statefulModal);
        }}
        modalProps={modalProps}
        showCloseButton={showCloseButton}
        iconButtonProps={iconButtonProps}
        buttons={buttons.map((button) => button(props, statefulModal))}
        children={children?.(props, statefulModal)}
        {...dialogProps}
      />
    );
  });

  return useModal(modal);
}

type ConfirmButton<T> = (
  onClick: () => void,
  props: T,
) => ReactElement<ButtonProps>;
export interface ConfirmDialogProps<T> extends BaseDialogProps<T> {
  cancelButton?: ConfirmButton<T> | string;
  primaryButton: ConfirmButton<T> | string;
  onClose?: (modal: NiceModalHandler) => void;
}
export interface ConfirmShowProps {
  description?: string;
  title?: string;
}

export type ConfirmModalProps = ConfirmShowProps & {
  primaryButton?: string;
  cancelButton?: string;
  size?: DialogProps["size"];
  showCloseButton?: boolean;
  buttonFormat?: DialogProps["buttonFormat"];
};

export const ConfirmModal = createModal<ConfirmModalProps>((props) => {
  const modal = useModal();
  const {
    title,
    description,
    primaryButton = "Confirm",
    cancelButton = "Cancel",
    size = "s",
    showCloseButton = false,
    buttonFormat = "horizontal",
  } = props;

  const handleCancel = () => {
    modal.resolve(false);
    modal.hide();
  };

  const handleConfirm = () => {
    modal.resolve(true);
    modal.hide();
  };

  return (
    <Dialog
      title={title}
      description={description}
      open={modal.visible}
      onOpenChange={(open) => {
        if (open) modal.show();
        else {
          modal.resolve(false);
          modal.remove();
        }
      }}
      showCloseButton={showCloseButton}
      buttons={[
        <Button key="cancel" onClick={handleCancel} variant="outline">
          {cancelButton}
        </Button>,
        <Button
          key="confirm"
          autoFocus
          data-testid={HallowDataTestId.DialogPrimaryButton}
          onClick={handleConfirm}
        >
          {primaryButton}
        </Button>,
      ]}
      size={size}
      buttonFormat={buttonFormat}
    />
  );
});

export function useConfirm<T = ConfirmShowProps>({
  title = (props) => (props as Record<string, any>)?.["title"],
  description = (props) => (props as Record<string, any>)?.["description"],
  primaryButton,
  cancelButton,
  modalProps,
  size = "s",
  showCloseButton = false,
  buttonFormat = "horizontal",
  onClose,
}: ConfirmDialogProps<T>) {
  const modal = createModal<T>((props) => {
    const modal = useModal();

    const handleCancel = () => {
      modal.resolve(false);
      modal.hide();
    };

    const handleConfirm = () => {
      modal.resolve(true);
      modal.hide();
    };

    const primaryButtonEl =
      typeof primaryButton === "string" ? (
        <Button
          autoFocus
          data-testid={HallowDataTestId.DialogPrimaryButton}
          onClick={handleConfirm}
        >
          {primaryButton}
        </Button>
      ) : (
        primaryButton(handleConfirm, props)
      );

    const cancelButtonEl =
      typeof cancelButton === "string" ? (
        <Button onClick={handleCancel} variant="outline">
          {cancelButton}
        </Button>
      ) : (
        cancelButton?.(handleCancel, props)
      );

    return (
      <Dialog
        title={title(props)}
        description={description?.(props)}
        open={modal.visible}
        onOpenChange={(open) => {
          if (open) modal.show();
          else if (onClose) {
            onClose(modal);
          } else {
            modal.resolve(false);
            modal.remove();
          }
        }}
        modalProps={modalProps}
        showCloseButton={showCloseButton}
        buttons={[cancelButtonEl, primaryButtonEl]}
        size={size}
        buttonFormat={buttonFormat}
      />
    );
  });

  return useModal(modal);
}

interface AlertDialogProps<T> extends BaseDialogProps<T> {
  button?: ConfirmButton<T> | string;
  onClose?: ConfirmDialogProps<T>["onClose"];
}

export function useAlert<T = ConfirmShowProps>({
  button,
  ...props
}: AlertDialogProps<T> = {}) {
  const t = useTranslations();
  return useConfirm({
    ...props,
    primaryButton: button ?? t("general_word_ok"),
  });
}

type PromptProps<T> = Omit<
  ConfirmDialogProps<T>,
  "cancelButton" | "primaryButton" | "description"
> & {
  promptQuestion: string | ReactNode;
  promptType: InputProps["type"];
  defaultValue?: string | number;
};
export function usePrompt<T>({
  promptQuestion,
  promptType,
  defaultValue = null,
  ...props
}: PromptProps<T>) {
  const promptRef = useRef<HTMLInputElement>(null);
  const modal = useConfirm<T>({
    ...props,
    cancelButton: "Cancel",
    primaryButton: () => (
      <Button
        onClick={() => {
          modal.resolve(promptRef.current.value);
          modal.remove();
        }}
      >
        OK
      </Button>
    ),
    description: () => (
      <>
        <Text>{promptQuestion}</Text>
        <Input type={promptType} ref={promptRef} value={defaultValue} />
      </>
    ),
  });
  return modal;
}
